<template>
  <main-buttons-bar :align="'space-between'">
    <template #left>
      <custom-title>Sentidos de bobinado personalizados</custom-title>
    </template>
    <template #right>
      <el-button
        type="primary"
        size="default"
        :icon="IconAdd"
        @click="abrirAlta"
        >Nuevo sentido</el-button
      >
    </template>
  </main-buttons-bar>

  <el-alert
    :title="
      esPersonalizada
        ? 'La configuración activa es Personalizada: estos sentidos son los que se ofrecen en las pantallas.'
        : 'La configuración activa es De sistema. Para usar estos sentidos, cambiá la opción en Configuración general > Producción e imposición.'
    "
    :type="esPersonalizada ? 'success' : 'info'"
    :closable="false"
    show-icon
    style="margin-bottom: 12px"
  />
  <p class="criterio-imagen">
    Imagen: PNG, JPG o WEBP de hasta 3 MB. Se ajusta automáticamente a un máximo de 440 × 540 px
    (formato vertical, fondo blanco o transparente).
  </p>

  <el-table
    :data="sentidoBobinadoPersonalizadoList"
    :style="{ width: '100%' }"
    empty-text="Sin sentidos personalizados"
    border
  >
    <el-table-column
      prop="Codigo"
      label="Código"
      width="100"
      sortable
    />
    <el-table-column
      label="Imagen"
      width="120"
      align="center"
    >
      <template #default="scope">
        <img
          :src="scope.row.ImagenURL"
          alt="sentido"
          class="miniatura"
        />
      </template>
    </el-table-column>
    <el-table-column
      prop="Descripcion"
      label="Descripción"
      sortable
    />
    <el-table-column
      label="Activo"
      width="100"
      align="center"
    >
      <template #default="scope">
        <el-tag :type="scope.row.Activo ? 'success' : 'info'">{{
          scope.row.Activo ? 'Sí' : 'No'
        }}</el-tag>
      </template>
    </el-table-column>
    <el-table-column
      label="Acciones"
      width="160"
      align="center"
    >
      <template #default="scope">
        <el-button
          :icon="IconEdit"
          @click.prevent="abrirEdicion(scope.row)"
        />
        <el-button
          :icon="IconDelete"
          :disabled="!scope.row.Activo"
          @click.prevent="darDeBaja(scope.row)"
        />
      </template>
    </el-table-column>
  </el-table>

  <el-dialog
    v-model="dialogVisible"
    :title="editando ? 'Editar sentido de bobinado' : 'Nuevo sentido de bobinado'"
    width="480px"
    @closed="resetForm"
  >
    <el-form
      label-position="top"
      @submit.prevent
    >
      <el-form-item label="Descripción">
        <el-input
          v-model="form.Descripcion"
          maxlength="200"
          placeholder="Ej: Embobinado exterior - salida derecha"
        />
      </el-form-item>
      <el-form-item label="Imagen">
        <div class="img-field">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :show-file-list="false"
            accept="image/png,image/jpeg,image/webp"
            :on-change="onPickImage"
          >
            <el-button size="default">{{
              previewUrl ? 'Cambiar imagen' : 'Elegir imagen'
            }}</el-button>
          </el-upload>
          <img
            v-if="previewUrl"
            :src="previewUrl"
            alt="Vista previa"
            class="preview"
          />
        </div>
      </el-form-item>
      <el-form-item
        v-if="editando"
        label="Activo"
      >
        <el-switch v-model="form.Activo" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">Cancelar</el-button>
      <el-button
        type="primary"
        @click="guardar"
        >Guardar</el-button
      >
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import IconAdd from '~icons/mdi/add';
import IconDelete from '~icons/mdi/delete';
import IconEdit from '~icons/mdi/edit';
import CustomTitle from '@/shared/components/custom_title.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import { useSentidoBobinadoStore } from '@/stores/sentido_bobinado_store.js';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';

const MIMES_VALIDOS = ['image/png', 'image/jpeg', 'image/webp'];
const PESO_MAX_MB = 3;

const sentidoStore = useSentidoBobinadoStore();
const { sentidoBobinadoPersonalizadoList } = storeToRefs(sentidoStore);
const { sentidoBobinadoConfiguracion } = storeToRefs(useConfiguracionStore());
const esPersonalizada = computed(() => sentidoBobinadoConfiguracion.value === 'personalizada');

const dialogVisible = ref(false);
const editando = ref(null);
const form = ref({ Descripcion: '', Activo: true });
const archivo = ref(null);
const previewUrl = ref('');
const uploadRef = ref(null);

function liberarPreview() {
  if (previewUrl.value.startsWith('blob:')) URL.revokeObjectURL(previewUrl.value);
}

function resetForm() {
  liberarPreview();
  editando.value = null;
  form.value = { Descripcion: '', Activo: true };
  archivo.value = null;
  previewUrl.value = '';
  uploadRef.value?.clearFiles();
}

function abrirAlta() {
  resetForm();
  dialogVisible.value = true;
}

function abrirEdicion(sentido) {
  resetForm();
  editando.value = sentido.Codigo;
  form.value = { Descripcion: sentido.Descripcion, Activo: sentido.Activo };
  previewUrl.value = sentido.ImagenURL || '';
  dialogVisible.value = true;
}

function onPickImage(uploadFile) {
  const file = uploadFile.raw;
  if (!file) return;
  if (!MIMES_VALIDOS.includes(file.type)) {
    useMessageError().open('Formato inválido. Use PNG/JPG/WEBP.');
    uploadRef.value?.clearFiles();
    return;
  }
  if (file.size / 1024 / 1024 > PESO_MAX_MB) {
    useMessageError().open(`El archivo supera ${PESO_MAX_MB}MB.`);
    uploadRef.value?.clearFiles();
    return;
  }
  liberarPreview();
  archivo.value = file;
  previewUrl.value = URL.createObjectURL(file);
}

async function recargar() {
  await sentidoStore.findPersonalizadosHTTP();
}

async function guardar() {
  const descripcion = form.value.Descripcion.trim();
  if (!descripcion) {
    useMessageError().open('La descripción es requerida.');
    return;
  }
  if (!editando.value && !archivo.value) {
    useMessageError().open('Seleccioná una imagen.');
    return;
  }
  try {
    useLoading().openLoading();
    if (editando.value) {
      await sentidoStore.updatePersonalizadoHTTP(editando.value, {
        Descripcion: descripcion,
        Activo: form.value.Activo,
        file: archivo.value,
      });
    } else {
      await sentidoStore.createPersonalizadoHTTP({ Descripcion: descripcion, file: archivo.value });
    }
    dialogVisible.value = false;
    await recargar();
    useMessageSuccess().open('El sentido de bobinado se guardó correctamente!');
  } catch (error) {
    useMessageError().open('Ha ocurrido un error al intentar guardar el sentido de bobinado.');
  } finally {
    useLoading().closeLoading();
  }
}

async function darDeBaja(sentido) {
  try {
    await ElMessageBox.confirm(
      `¿Dar de baja "${sentido.Descripcion}"? Los registros existentes que lo usan no se modifican.`,
      'Confirmar baja',
      { confirmButtonText: 'Dar de baja', cancelButtonText: 'Cancelar', type: 'warning' },
    );
  } catch {
    return;
  }
  try {
    useLoading().openLoading();
    await sentidoStore.deletePersonalizadoHTTP(sentido.Codigo);
    await recargar();
    useMessageSuccess().open('El sentido de bobinado fue dado de baja.');
  } catch (error) {
    useMessageError().open('Ha ocurrido un error al intentar dar de baja el sentido.');
  } finally {
    useLoading().closeLoading();
  }
}

onBeforeMount(recargar);
</script>

<style scoped>
.criterio-imagen {
  margin: 0 0 12px;
  color: #6b7280;
  font-size: 13px;
}
.miniatura {
  height: 56px;
  width: auto;
  max-width: 90px;
  object-fit: contain;
  background: #fff;
}
.img-field {
  display: flex;
  align-items: center;
  gap: 16px;
}
.preview {
  height: 120px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #ebeef0;
  border-radius: 4px;
}
</style>
